import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CheckCircleIcon, XCircleIcon } from '@/components/icons';
import { pluralize } from '@/utils/format';
import type { MonitorResult, MonitorSummaryResponse } from '@/types/certificate';

export interface MonitoringResultsModalProps {
  /** Result to show; the modal is open while it is set */
  result: MonitorSummaryResponse | null;
  onClose: () => void;
}

function resultClassName(result: MonitorResult): string {
  if (result.error) return 'certs-monitoring-item-error';
  if (result.updated) return 'certs-monitoring-item-updated';
  if (result.changed) return 'certs-monitoring-item-changed';
  return 'certs-monitoring-item-unchanged';
}

/**
 * Summary and per-certificate details of a monitoring run
 */
export function MonitoringResultsModal({ result, onClose }: MonitoringResultsModalProps) {
  return (
    <Modal isOpen={result !== null} onClose={onClose}>
      <ModalHeader>Certificate Monitoring Results</ModalHeader>
      <ModalBody>
        {result && (
          <div className="certs-monitoring-results">
            <div className="certs-monitoring-summary">
              <div className="certs-monitoring-stat">
                <span className="certs-monitoring-stat-value">{result.total_certificates}</span>
                <span className="certs-monitoring-stat-label">Checked</span>
              </div>
              <div className="certs-monitoring-stat certs-monitoring-stat-changed">
                <span className="certs-monitoring-stat-value">{result.changed}</span>
                <span className="certs-monitoring-stat-label">Changed</span>
              </div>
              <div className="certs-monitoring-stat certs-monitoring-stat-updated">
                <span className="certs-monitoring-stat-value">{result.updated}</span>
                <span className="certs-monitoring-stat-label">Updated</span>
              </div>
              <div className="certs-monitoring-stat certs-monitoring-stat-errors">
                <span className="certs-monitoring-stat-value">{result.errors}</span>
                <span className="certs-monitoring-stat-label">Errors</span>
              </div>
            </div>

            <div className="certs-monitoring-meta">
              <span>Duration: {result.duration}</span>
            </div>

            {result.results.length > 0 ? (
              <div className="certs-monitoring-details">
                <h4 className="certs-monitoring-details-title">Details</h4>
                <div className="certs-monitoring-list">
                  {result.results.map((item) => (
                    <div
                      key={item.certificate_id}
                      className={`certs-monitoring-item ${resultClassName(item)}`}
                    >
                      <div className="certs-monitoring-item-icon">
                        {item.error ? <XCircleIcon /> : <CheckCircleIcon />}
                      </div>
                      <div className="certs-monitoring-item-content">
                        <div className="certs-monitoring-item-domain">{item.domain_name}</div>
                        <div className="certs-monitoring-item-node">
                          Node: {item.node_name || item.node_id}
                        </div>
                        {item.error && <div className="certs-monitoring-item-error">{item.error}</div>}
                        {item.updated && (
                          <div className="certs-monitoring-item-success">
                            Updated and synced to {pluralize(item.synced_to_nodes, 'node')}
                          </div>
                        )}
                        {!item.changed && !item.error && (
                          <div className="certs-monitoring-item-unchanged-text">No changes</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="certs-monitoring-empty">No certificates with source nodes to monitor.</div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
